import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';
import {
  checkBackofficeOrganizationSlugAvailability,
  createBackofficeOrganization,
  fetchBackofficeTierTemplates,
  updateBackofficeOrganization,
} from '../api';
import type {
  BackofficeTierTemplate,
  BackofficeOrganizationDetail,
  BackofficeOrganizationDetailResponse,
  UpdateOrganizationInput,
  CreateOrganizationInput,
  CreateOrganizationMemberInput,
  CreateOrganizationResponse,
  DefaultStudyAccess,
  PricingTier,
} from '../types';
import { OrganizationFeatureFields, featuresEqual } from './OrganizationFeatureFields';
import { getTemplateFeatures, DEFAULT_FEATURES } from './organization-editor-form';
import { OrganizationMemberEditor } from './OrganizationMemberEditor';
import {
  formatOrganizationMemberDelivery,
  formatOrganizationMemberRole,
} from './organization-member-form';

const DEFAULT_TIER: PricingTier = 'free_1';

const ACCESS_OPTIONS: Array<{ value: DefaultStudyAccess; label: string }> = [
  { value: 'none', label: 'No default access' },
  { value: 'viewer', label: 'Study viewer' },
  { value: 'editor', label: 'Study editor' },
  { value: 'co_owner', label: 'Study co-owner' },
];

type OrganizationWizardModalProps = { onClose: () => void } & (
  | { mode: 'create'; onCreated: (response: CreateOrganizationResponse) => void }
  | {
      mode: 'update';
      organization: BackofficeOrganizationDetail;
      onUpdated: (response: BackofficeOrganizationDetailResponse) => void;
      onRefresh: () => Promise<void>;
    }
);

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function formatTierLabel(pricingTier: PricingTier): string {
  if (pricingTier === 'payg_1') return 'Pay As You Go';
  if (pricingTier === 'ent_1') return 'Enterprise';
  return 'Free';
}

export function OrganizationWizardModal(props: OrganizationWizardModalProps) {
  const { onClose } = props;
  const [initialOrganization] = useState(() =>
    props.mode === 'update' ? props.organization : null
  );
  const editing = props.mode === 'update';
  const savedOrganization = props.mode === 'update' ? props.organization : null;
  const lastStep = editing ? 1 : 2;
  const [step, setStep] = useState(0);
  const [name, setName] = useState(initialOrganization?.name ?? '');
  const [slug, setSlug] = useState(initialOrganization?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(editing);
  const [tiers, setTiers] = useState<BackofficeTierTemplate[]>([]);
  const [pricingTier, setPricingTier] = useState<PricingTier>(
    initialOrganization?.pricingTier ?? DEFAULT_TIER
  );
  const [memberDefaultStudyAccess, setMemberDefaultStudyAccess] = useState<DefaultStudyAccess>(
    initialOrganization?.membershipPermissions.memberDefaultStudyAccess ?? 'viewer'
  );
  const [adminDefaultStudyAccess, setAdminDefaultStudyAccess] = useState<DefaultStudyAccess>(
    initialOrganization?.membershipPermissions.adminDefaultStudyAccess ?? 'co_owner'
  );
  const [defaultVoiceProvider, setDefaultVoiceProvider] = useState<'vapi' | 'elevenlabs'>(
    initialOrganization?.settings.defaultVoiceProvider === 'vapi' ? 'vapi' : 'elevenlabs'
  );
  const [features, setFeatures] = useState(initialOrganization?.features ?? DEFAULT_FEATURES);
  const [projectRetentionDays, setProjectRetentionDays] = useState<number | null>(
    initialOrganization ? initialOrganization.projectRetentionDays : 30
  );
  const [customerSuccessContactLimit, setCustomerSuccessContactLimit] = useState<number | null>(
    initialOrganization?.customerSuccessContactLimit ?? null
  );
  const [members, setMembers] = useState<CreateOrganizationMemberInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const selectedTier = tiers.find((tier) => tier.pricingTier === pricingTier);
  const suggestedSlug = slugify(name);
  const hasOwner = members.some(
    (member) =>
      member.role === 'owner' && member.delivery === 'direct' && member.kind === 'existing'
  );

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting && !checkingSlug) {
        onClose();
      }
      if (event.key === 'Tab') {
        const elements = Array.from(
          modalRef.current?.querySelectorAll<HTMLElement>(
            'button, input, select, [tabindex="0"]'
          ) ?? []
        ).filter((element) => !element.matches(':disabled'));
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting, checkingSlug]);

  useEffect(() => {
    if (!slugTouched) {
      setSlug(suggestedSlug);
    }
  }, [slugTouched, suggestedSlug]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBackofficeTierTemplates(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setTiers(response.tiers);
        const tier = response.tiers.find((candidate) => candidate.pricingTier === DEFAULT_TIER);
        if (tier && !initialOrganization) {
          setFeatures(getTemplateFeatures(tier));
          setProjectRetentionDays(tier.projectRetentionDays);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Failed to load tier templates');
      });
    return () => controller.abort();
  }, [initialOrganization]);

  const stepErrors = useMemo(() => {
    const errors: Record<number, string | null> = {
      0: null,
      1: null,
      2: null,
    };

    if (!name.trim()) {
      errors[0] = 'Organization name is required.';
    } else if (!slug.trim() && !(editing && initialOrganization?.slug === null)) {
      errors[0] = 'Slug is required.';
    } else if (slug.trim() && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim())) {
      errors[0] = 'Slug can only contain lowercase letters, numbers, and hyphens.';
    }

    if (
      projectRetentionDays !== null &&
      (!Number.isInteger(projectRetentionDays) || projectRetentionDays < 1)
    ) {
      errors[1] = 'Project retention period must be a positive number of days.';
    } else if (
      customerSuccessContactLimit !== null &&
      (!Number.isInteger(customerSuccessContactLimit) || customerSuccessContactLimit < 0)
    ) {
      errors[1] = 'Contact allowance must be a whole number of contacts, zero or more.';
    }

    if (!editing && !hasOwner) {
      errors[2] = 'Add one existing user as owner before creating the organization.';
    }

    return errors;
  }, [
    customerSuccessContactLimit,
    editing,
    initialOrganization,
    hasOwner,
    name,
    projectRetentionDays,
    slug,
  ]);

  function selectTier(nextPricingTier: PricingTier) {
    setPricingTier(nextPricingTier);
    const template = tiers.find((tier) => tier.pricingTier === nextPricingTier);
    if (template && !editing) {
      setFeatures(getTemplateFeatures(template));
      setProjectRetentionDays(template.projectRetentionDays);
    }
  }

  function validateAllSteps(): string | null {
    return stepErrors[0] ?? stepErrors[1] ?? stepErrors[2] ?? null;
  }

  const dirty =
    !savedOrganization ||
    name.trim() !== savedOrganization.name ||
    slug.trim() !== (savedOrganization.slug ?? '') ||
    pricingTier !== savedOrganization.pricingTier ||
    memberDefaultStudyAccess !== savedOrganization.membershipPermissions.memberDefaultStudyAccess ||
    adminDefaultStudyAccess !== savedOrganization.membershipPermissions.adminDefaultStudyAccess ||
    defaultVoiceProvider !== (savedOrganization.settings.defaultVoiceProvider ?? 'elevenlabs') ||
    projectRetentionDays !== savedOrganization.projectRetentionDays ||
    customerSuccessContactLimit !== savedOrganization.customerSuccessContactLimit ||
    !featuresEqual(features, savedOrganization.features);

  async function handleSubmit() {
    if (submitting || checkingSlug || !dirty || !tiers.length) return;
    const validationError = validateAllSteps();
    if (validationError) {
      setError(validationError);
      return;
    }

    const input: UpdateOrganizationInput = {
      name: name.trim(),
      ...(slug.trim() ? { slug: slug.trim() } : {}),
      pricingTier,
      defaultVoiceProvider,
      membershipPermissions: {
        memberDefaultStudyAccess,
        adminDefaultStudyAccess,
      },
      features,
      projectRetentionDays,
    };

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    setWarnings([]);
    try {
      if (props.mode === 'update') {
        const response = await updateBackofficeOrganization(props.organization.id, {
          ...input,
          customerSuccessContactLimit,
        });
        props.onUpdated(response);
        onClose();
      } else {
        const createInput: CreateOrganizationInput = {
          ...input,
          domainFeatureEnabled: features.domains,
          members,
        };
        const response = await createBackofficeOrganization(createInput);
        setSuccess(`${response.organization.name} was created.`);
        setWarnings(response.warnings);
        props.onCreated(response);
      }
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Failed to save organization');
      if (props.mode === 'update') await props.onRefresh().catch(() => undefined);
    } finally {
      setSubmitting(false);
    }
  }

  async function goNext() {
    const validationError = stepErrors[step];
    if (validationError) {
      setError(validationError);
      return;
    }

    if (step === 0 && slug.trim() && slug.trim() !== savedOrganization?.slug) {
      setCheckingSlug(true);
      setError(null);
      try {
        const response = await checkBackofficeOrganizationSlugAvailability(slug.trim());
        if (!response.available) {
          setError('Slug is already in use. Choose a different slug before continuing.');
          return;
        }
      } catch (caught: unknown) {
        setError(caught instanceof Error ? caught.message : 'Failed to validate slug');
        return;
      } finally {
        setCheckingSlug(false);
      }
    }

    setError(null);
    setStep((current) => Math.min(current + 1, lastStep));
  }

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-organization-title"
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="shrink-0 border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <h2
              id="create-organization-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              {editing ? 'Update organisation' : 'Create new organization'}
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              disabled={submitting || checkingSlug}
              className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="mt-4 flex justify-center text-small text-neutral-500">
            <div className="flex items-center gap-3">
              {Array.from({ length: lastStep + 1 }, (_, index) => index).map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span
                    className={`flex size-7 items-center justify-center rounded-full border text-xs font-semibold ${
                      item <= step
                        ? 'border-backoffice-500 bg-backoffice-500 text-white'
                        : 'border-neutral-300 bg-white text-neutral-500'
                    }`}
                  >
                    {item + 1}
                  </span>
                  {item < lastStep ? <span className="h-px w-20 bg-neutral-300 sm:w-28" /> : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto px-6 py-5">
          {error ? (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small text-red-800"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
          {success ? (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-small text-emerald-800">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
              <span>{success}</span>
            </div>
          ) : null}
          {warnings.map((warning) => (
            <div
              key={warning}
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-small text-amber-800"
            >
              {warning}
            </div>
          ))}

          <fieldset disabled={submitting || checkingSlug || !tiers.length}>
            {step === 0 ? (
              <div className="space-y-5">
                <h3 className="font-inter-tight text-lg font-semibold text-neutral-900">
                  General information
                </h3>
                <label className="block">
                  <span className="text-small font-medium text-neutral-700">Name</span>
                  <input
                    value={name}
                    maxLength={160}
                    onChange={(event) => setName(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                    placeholder="Acme Research"
                  />
                </label>
                <label className="block">
                  <span className="text-small font-medium text-neutral-700">Slug</span>
                  <input
                    value={slug}
                    maxLength={120}
                    onChange={(event) => {
                      setSlugTouched(true);
                      setSlug(slugify(event.target.value));
                    }}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                    placeholder="acme-research"
                  />
                </label>
                <label className="block">
                  <span className="text-small font-medium text-neutral-700">Tier</span>
                  <select
                    value={pricingTier}
                    onChange={(event) => selectTier(event.target.value as PricingTier)}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                  >
                    {(tiers.length > 0 ? tiers : [{ pricingTier: DEFAULT_TIER, name: 'Free' }]).map(
                      (tier) => (
                        <option key={tier.pricingTier} value={tier.pricingTier}>
                          {tier.name}
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>
            ) : null}

            {step === 1 ? (
              <div className="space-y-5">
                <h3 className="font-inter-tight text-lg font-semibold text-neutral-900">
                  Tier settings
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block">
                    <span className="text-small font-medium text-neutral-700">
                      Member study access
                    </span>
                    <select
                      value={memberDefaultStudyAccess}
                      onChange={(event) =>
                        setMemberDefaultStudyAccess(event.target.value as DefaultStudyAccess)
                      }
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                    >
                      {ACCESS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-small font-medium text-neutral-700">
                      Co-owner study access
                    </span>
                    <select
                      value={adminDefaultStudyAccess}
                      onChange={(event) =>
                        setAdminDefaultStudyAccess(event.target.value as DefaultStudyAccess)
                      }
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                    >
                      {ACCESS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="text-small font-medium text-neutral-700">
                    Default voice assistant
                  </span>
                  <select
                    value={defaultVoiceProvider}
                    onChange={(event) =>
                      setDefaultVoiceProvider(event.target.value as 'vapi' | 'elevenlabs')
                    }
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                  >
                    <option value="elevenlabs">ElevenLabs</option>
                    <option value="vapi">VAPI</option>
                  </select>
                </label>
                <p className="text-small text-neutral-500">
                  Used for all new studies. Existing studies keep their voice assistant.
                </p>
                <label className="block">
                  <span className="text-small font-medium text-neutral-700">
                    Project retention period
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={projectRetentionDays ?? ''}
                    onChange={(event) => {
                      const value = event.target.value.trim();
                      setProjectRetentionDays(value ? Number(value) : null);
                    }}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                    placeholder={pricingTier === 'ent_1' ? 'No retention limit' : 'Tier default'}
                  />
                  {selectedTier && projectRetentionDays !== selectedTier.projectRetentionDays ? (
                    <span className="mt-2 block rounded-md bg-amber-50 px-3 py-2 text-small font-medium text-amber-800">
                      Differs from tier default:{' '}
                      {selectedTier.projectRetentionDays === null
                        ? 'No retention limit'
                        : `${selectedTier.projectRetentionDays} days`}
                    </span>
                  ) : null}
                  {projectRetentionDays === null && pricingTier !== 'ent_1' ? (
                    <span className="mt-1 block text-small text-neutral-500">
                      An empty value uses the tier's default retention period.
                    </span>
                  ) : null}
                </label>
                <section
                  aria-label="Organization features"
                  className="overflow-hidden rounded-lg border border-neutral-200"
                >
                  <h3 className="px-5 pt-4 font-inter-tight text-lg font-semibold text-neutral-900">
                    Organization features
                  </h3>
                  <OrganizationFeatureFields
                    features={features}
                    defaults={selectedTier ? getTemplateFeatures(selectedTier) : undefined}
                    onChange={setFeatures}
                    contactLimit={
                      editing
                        ? {
                            value: customerSuccessContactLimit,
                            onChange: setCustomerSuccessContactLimit,
                          }
                        : undefined
                    }
                  />
                </section>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="space-y-5">
                <h3 className="font-inter-tight text-lg font-semibold text-neutral-900">Members</h3>
                <OrganizationMemberEditor
                  members={members}
                  onMembersChange={setMembers}
                  allowedRoles={['owner', 'admin', 'member']}
                />

                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                  <h4 className="text-small font-semibold text-neutral-900">Summary</h4>
                  <dl className="mt-3 grid gap-3 text-small md:grid-cols-2">
                    <div>
                      <dt className="text-neutral-500">Organization</dt>
                      <dd className="font-medium text-neutral-900">{name || 'Not set'}</dd>
                    </div>
                    <div>
                      <dt className="text-neutral-500">Slug</dt>
                      <dd className="font-medium text-neutral-900">{slug || 'Not set'}</dd>
                    </div>
                    <div>
                      <dt className="text-neutral-500">Tier</dt>
                      <dd className="font-medium text-neutral-900">
                        {selectedTier?.name ?? formatTierLabel(pricingTier)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-neutral-500">Retention</dt>
                      <dd className="font-medium text-neutral-900">
                        {projectRetentionDays
                          ? `${projectRetentionDays} days`
                          : 'No retention limit'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-neutral-500">Domains</dt>
                      <dd className="font-medium text-neutral-900">
                        {features.domains ? 'Enabled' : 'Disabled'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-neutral-500">Members</dt>
                      <dd className="font-medium text-neutral-900">{members.length}</dd>
                    </div>
                  </dl>
                  {members.length > 0 ? (
                    <ul className="mt-3 space-y-1 text-small text-neutral-700">
                      {members.map((member) => (
                        <li key={`${member.email}-${member.userId ?? member.delivery}`}>
                          {member.email} - {formatOrganizationMemberRole(member.role)} -{' '}
                          {formatOrganizationMemberDelivery(member.delivery)}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : null}
          </fieldset>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setStep((current) => Math.max(current - 1, 0));
            }}
            disabled={step === 0 || submitting || checkingSlug}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-small font-medium text-neutral-700 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Back
          </button>
          <div className="flex items-center gap-2">
            {step < lastStep ? (
              <button
                type="button"
                onClick={() => void goNext()}
                disabled={submitting || checkingSlug || !tiers.length}
                className="rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {checkingSlug ? 'Checking slug' : 'Continue'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting || !dirty || !tiers.length}
                className="rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editing
                  ? submitting
                    ? 'Saving changes'
                    : 'Save changes'
                  : submitting
                    ? 'Creating organization'
                    : 'Create organization'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
